/**
 * The finder's result card.
 *
 * Split out of `assets/vsr-screen-finder.js` because that file sits 25 bytes
 * under the 20,480 B `AssetSizeJavaScript` error threshold, and the grading
 * below could not be written inside it. The core resolves four answers to a
 * fitment; this file decides what the card is ENTITLED TO CLAIM about that
 * resolution, and hides everything that is not earned.
 *
 * One rule governs every branch here: a check may only ever WITHHOLD a claim.
 * Nothing in this file can upgrade one. If it loads late, fails to parse, or is
 * blocked outright, the card keeps the state Liquid rendered it in — which is
 * the enquiry ending, with no price and no buy button. Failing closed is the
 * whole reason the grading lives on this side of the split.
 *
 * `window.VSRFinderResult`, not a `vsr:finder:*` event, and not `window.VSR`:
 * the namespace is frozen (`assets/global.js`), and a document-level listener
 * would double-fire when the Theme Editor re-injects the section. A plain
 * function the core calls at render time has neither problem and costs less.
 */
(function () {
  'use strict';

  /**
   * WHY THE PART NUMBER IS THE GATE, AND NOT THE CANDIDATE COUNT.
   *
   * A make, a model and a model year identify a FITMENT. They do not identify
   * the unit in the car. A 2017 Transporter left the factory with Composition
   * Colour, Composition Media or Discover Media — three units, three screen
   * sizes, three part numbers — and the catalogue carries a single row for that
   * model year. Being the only row the resolver can find is not evidence that
   * it is the only unit that fits; it is evidence that one row was entered.
   *
   * `confidence: documented` is the same doubt written down on the rows where
   * the workshop already knows it applies. It is not a substitute for this
   * gate: a `verified` row is verified about the SPAN, never about the unit.
   *
   * So "is there exactly one candidate?" cannot be the test. On this data it is
   * true on every single lookup, including all the wrong ones.
   *
   * The part number is the evidence. A fitment carrying one has been matched
   * against a physical label by someone who had the unit on the bench. A
   * fitment without one is a plausible guess the workshop still has to confirm
   * when the screen arrives. The badge is therefore gated on the part number
   * and on nothing else — and since no fitment in this store has one today, the
   * badge stops appearing entirely. That is the correct behaviour, not a
   * regression: it was previously shown on every result the finder produced.
   */
  function isIdentified(fitment) {
    return Boolean(fitment && fitment.part);
  }

  window.VSRFinderResult = function renderResult(finder, node) {
    if (!node) return;

    const set = (selector, text) => {
      const el = node.querySelector(selector);
      if (el) el.textContent = text || '';
    };
    // Optional parts of the card are `{% if %}`-ed out in Liquid, so a miss is
    // normal and both helpers take one silently.
    const show = (selector, on) => {
      const el = node.querySelector(selector);
      if (el) el.hidden = !on;
    };

    const issue = finder.issueById(finder.selection.issue);
    // Empty on the unknown path, so reads miss instead of crashing.
    const fitment = finder.resolveFitment() || (finder.unknown ? {} : null);

    // A return that leaves the card alone leaves the PREVIOUS resolution on
    // screen — a vehicle, a price and a buy button belonging to answers that
    // have since been cleared. Every claim comes down before the return.
    if (!issue || !fitment) {
      [
        '[data-result-match]',
        '[data-result-badge]',
        '[data-result-coverage]',
        '[data-result-turnaround]',
        '[data-result-assurances]',
        '[data-result-includes-wrap]',
        '[data-result-unit-figure]',
        '[data-result-price-block]',
        '[data-cart-form]',
        '[data-cta-link]'
      ].forEach((s) => show(s, false));
      set('[data-result-issue]', '');
      set('[data-result-part]', '');
      set('[data-result-price]', '');
      show('[data-result-enquiry]', true);
      renderCta(finder, node, show, issue, fitment, true, [], '');
      return;
    }

    // From the answers, not the fitment: on the unknown path they are all there
    // is. Empty only when the make was unlisted.
    const vehicle = [finder.selection.brand, finder.selection.model].filter(Boolean).join(' ');
    set('[data-result-vehicle]', vehicle);
    show('[data-result-match]', vehicle);
    show('[data-result-badge]', vehicle && isIdentified(fitment));
    // A different axis from the badge: the year is right and the unit is only
    // in part of that run. Absent on the unknown path, where `fitment` is {}.
    show('[data-result-coverage]', fitment.confidence === 'documented');

    // Leads with the name a customer recognises; the codename is a tag beside
    // it. `systemName` falls back to the whole string.
    set(
      '[data-result-meta]',
      [finder.selection.year, fitment.systemName || fitment.system].filter(Boolean).join(' · ')
    );
    // The unit's codename when we know the unit, the VEHICLE platform when we
    // do not. Both are the same kind of fact in the same mono tag — a code the
    // customer can check against their own car — and only one is ever set.
    set('[data-result-platform]', fitment.systemCode || fitment.platform);
    set('[data-result-issue]', issue.label);

    // The catalogue's number, or the one the visitor read off their own label
    // and <vsr-finder-lookup> matched against the verified list. Both are real,
    // and both belong on the order — but only the catalogue's lights the badge
    // above, because a part number does not identify the CAR. The full reason
    // is at the top of `assets/vsr-finder-lookup.js`.
    const known = fitment.part || finder.suppliedPart || '';

    // A code sets in mono; `part_unknown` is a sentence, and a sentence in mono
    // reads as a machine that failed to find one.
    const part = node.querySelector('[data-result-part]');
    if (part) {
      part.textContent = known || finder.string('part_unknown');
      part.classList.toggle('vsr-mono', Boolean(known));
    }

    // Marks are revealed, never built from a name. Card and panel, one query.
    node.querySelectorAll('[data-calc-brand]').forEach((mark) => {
      mark.hidden = mark.dataset.calcBrand !== finder.selection.brand;
    });

    // The fitment's own photo or no band; the section upload is the backdrop
    // now. An incomplete `w`/`h` pair is left alone, not guessed.
    const img = node.querySelector('[data-result-unit]');
    const hasUnit = Boolean(fitment.unit && fitment.unit.src);
    if (img && hasUnit) {
      img.src = fitment.unit.src;
      if (fitment.unit.w > 0 && fitment.unit.h > 0) {
        img.width = fitment.unit.w;
        img.height = fitment.unit.h;
      }
    }
    show('[data-result-unit-figure]', hasUnit);

    const includes = (issue.includes || [])
      .map((line) =>
        String(line)
          .replace(/<[^>]*>/g, '')
          .trim()
      )
      .filter(Boolean);
    const list = node.querySelector('[data-result-includes]');
    if (list) {
      list.textContent = '';
      includes.forEach((line) => {
        const item = document.createElement('li');
        item.className = 'vsr-finder__include';
        item.textContent = line;
        list.append(item);
      });
    }
    show('[data-result-includes-wrap]', includes.length);

    // Fitment price beats issue price when a unit is special. An unidentified
    // vehicle is not priced: that quotes a repair on a unit we have not
    // confirmed we cover.
    const price = (fitment.price || issue.price || '').trim();
    const isEnquiry = Boolean(issue.enquiry) || !price || Boolean(finder.unknown);
    set('[data-result-price]', price);

    show('[data-result-price-block]', !isEnquiry);
    show('[data-result-enquiry]', isEnquiry);

    // A turnaround and three guarantees are terms of a SALE. On the enquiry
    // ending there is no sale yet — no unit identified, no price quoted — and
    // promising a bench time for a repair we have not agreed to do is a claim
    // the card cannot support. They travel with the price.
    show('[data-result-turnaround]', !isEnquiry);
    show('[data-result-assurances]', !isEnquiry);

    renderCta(finder, node, show, issue, fitment, isEnquiry, includes, known);
  };

  /** Three endings, one shown — which and why, above the markup in Liquid. */
  function renderCta(finder, node, show, issue, fitment, isEnquiry, includes, known) {
    const form = node.querySelector('[data-cart-form]');
    const purchasable = !isEnquiry && Boolean(issue.variantId) && issue.available !== false;

    if (form) {
      const variantInput = form.querySelector('[data-cart-variant]');

      if (purchasable) {
        if (variantInput) variantInput.value = String(issue.variantId);

        const props = {
          // `${a} ${b}` on the unknown path composes the string "undefined
          // undefined" and posts it to the order. Present values only.
          //
          // The platform code rides in the vehicle string rather than in a
          // property of its own. `system` is the field that used to identify
          // the hardware on the order and it is blank on the whole verified
          // catalogue — we do not know which unit these cars carry — so
          // "Volkswagen Transporter (7E / SG / SH)" is the most identifying
          // line the workshop gets until the part-number mapping exists.
          vehicle: [
            [fitment.brand, fitment.model].filter(Boolean).join(' '),
            fitment.platform ? `(${fitment.platform})` : ''
          ]
            .filter(Boolean)
            .join(' '),
          year: finder.selection.year || '',
          system: fitment.system || '',
          part: known || '',
          includes: (includes || []).join(' · '),
          fitment: fitment.id || ''
        };

        // An empty property ships a blank row at checkout, so an unset one is
        // disabled rather than sent.
        form.querySelectorAll('[data-cart-prop]').forEach((input) => {
          input.value = props[input.dataset.cartProp] || '';
          input.disabled = !input.value;
        });
      } else {
        // Hiding the form does not empty it. A variant id and a vehicle left
        // behind by an earlier resolution stay in the DOM, and `hidden` is one
        // devtools toggle — or one CSS regression — away from being postable.
        // Nothing that is not on offer keeps a payload.
        if (variantInput) variantInput.value = '';
        form.querySelectorAll('[data-cart-prop]').forEach((input) => {
          input.value = '';
          input.disabled = true;
        });
      }
    }

    show('[data-cart-form]', purchasable);
    show('[data-cta-enquiry]', isEnquiry);
    show('[data-cta-link]', !purchasable && !isEnquiry);
  }
})();
